import React from "react";
import PropTypes from "prop-types";
import { findIndex, get } from "lodash";
import clsx from "clsx";
import { Button, makeStyles, useMediaQuery } from "@material-ui/core";
import { t } from "@configuratorware/configurator-frontendgui/Framework/i18n";
import DialogContent from "@material-ui/core/DialogContent";
import LoadingOverlay from "@configuratorware/configurator-frontendgui/Framework/Components/LoadingOverlay";
import CustomDialog from "@configuratorware/configurator-frontendgui/Shared/Components/CustomDialog";
import customClassName from "@configuratorware/configurator-frontendgui/App/Utils/customClassName";
import ComponentDescription from "../ComponentDescription";
import { useDispatch, useSelector } from "react-redux";
import { setOptionFilter } from "../../Shared/Actions";

const useStyles = makeStyles(
  (theme: any) => {
    return {
      filters: {
        display: "flex",
        flexDirection: "row !important",
        flexWrap: "wrap",
        gap: 8,
        position: "sticky",
        top: 0,
        zIndex: 1,
        background: "#fff",
        paddingBottom: theme.spacing(1),
      },
      filterButton: {
        minWidth: "auto !important",
        padding: "12px 24px",
        fontWeight: 600,
        color: theme.palette.secondary.main,
        borderColor: theme.palette.secondary.main,
        "&.is-selected": {
          color: "#fff",
          backgroundColor: "#333",
        },
      },
      dialogStyle: {
        height: "100%",
        [theme.breakpoints.down("sm")]: {
          width: "100%",
          maxWidth: "390px",
        },
        [theme.breakpoints.between("sm", "xl")]: {
          width: "470px",
          maxWidth: "470px",
        },
        [theme.breakpoints.up("md")]: {
          marginLeft: "120px",
        },
      },
      [theme.breakpoints.between("sm", "md")]: {
        [theme.orientation.portrait]: {
          top: "-110px !important",
        },
      },
      "& > div.MuiDialog-container": {
        [theme.breakpoints.between("sm", "lg")]: {
          [theme.orientation.portrait]: {
            alignItems: "flex-end",
          },
          [theme.orientation.landscape]: {
            alignItems: "flex-start",
            marginTop: 35,
          },
        },
      },
      contentStyle: {
        paddingTop: 0,
      },
      backDrop: {
        backgroundColor: "rgba(0,0,0,0.0)",
      },
      description: {
        marginBottom: 10,
        paddingLeft: 32,
        paddingRight: 32,
      },
      dialogBorderStyle: {
        alignSelf: "flex-end",
      },
      applyButton: {
        width: "90%",
      },
      applyButtonContainer: {
        position: "sticky",
        height: 70,
        width: "100%",
        background: "#fff",
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    };
  },
  {
    name: "CustomOptionsList",
  },
);

const OptionsList = ({
  addCheckedOptionclassification,
  children,
  isLoading,
  onClose,
  onSelect,
  onToggle,
  optionclassification,
  options,
  show,
  viewDetails,
  viewStockInformation,
  LoadingComponent,
  OptionComponent,
  isAdminMode,
}) => {
  const {
    selectedComponentFilters,
    activeComponentFilter,
    filteredComponentIdentifier,
  } = useSelector((state: any) => ({
    selectedComponentFilters: state.configurator.selectedComponentFilters,
    activeComponentFilter: state.configurator.activeComponentFilter,
    filteredComponentIdentifier: state.configurator.filteredComponentIdentifier,
  }));

  const dispatch = useDispatch();
  const classes = useStyles({});

  const isXSmall = useMediaQuery("(max-width: 375px)");

  const toggleRemoteFilter = (filterIdentifier) => {
    dispatch(
      setOptionFilter(optionclassification.identifier, filterIdentifier),
    );
  };

  const createOptions = () => {
    const onOptionClicked = () => {
      addCheckedOptionclassification(optionclassification);
    };

    return options.map((option) => {
      let optionIndex = findIndex(
        get(optionclassification, "selectedoptions"),
        {
          identifier: option.identifier,
        },
      );

      const selected =
        optionIndex > -1 && !(option.amount_is_selectable && option.amount < 1);

      return (
        <OptionComponent
          key={"optionlistitem" + option.identifier}
          option={option}
          optionclassification={optionclassification}
          onSelect={onSelect}
          selected={selected}
          viewDetails={viewDetails}
          viewStockInformation={viewStockInformation}
          onClick={onOptionClicked}
          onToggle={onToggle}
          isAdminMode={isAdminMode}
        />
      );
    });
  };

  const renderOptions = () => {
    const optionListItems = createOptions();

    return (
      <div>
        {children
          ? children
          : optionListItems.length > 0
            ? optionListItems
            : null}
        <LoadingComponent show={isLoading} />
      </div>
    );
  };

  const renderRemoteFilters = () => {
    // use filters loaded from api
    // reload options when changing filter

    if (
      selectedComponentFilters.length === 0 ||
      optionclassification.identifier !== filteredComponentIdentifier
    )
      return null;

    return (
      <div className={classes.filters}>
        {selectedComponentFilters.map((filter) => (
          <Button
            variant="outlined"
            disableRipple={true}
            className={clsx(classes.filterButton, {
              "is-selected": filter.identifier === activeComponentFilter,
            })}
            key={filter.identifier}
            onClick={() => toggleRemoteFilter(filter.identifier)}
          >
            {filter.label}
          </Button>
        ))}
      </div>
    );
  };

  const renderDescription = () => {
    return (
      optionclassification.description && (
        <ComponentDescription
          text={optionclassification.description}
          className={classes.description}
        />
      )
    );
  };

  const renderApplyButton = () => {
    return (
      <div className={classes.applyButtonContainer}>
        <Button
          onClick={onClose}
          className={classes.applyButton}
          variant="contained"
          color="secondary"
        >
          {t("custom.button.apply")}
        </Button>
      </div>
    );
  };

  return (
    <CustomDialog
      className={clsx(classes.dialogStyle, customClassName("options"))}
      dialogTitle={optionclassification.title}
      onCancel={onClose}
      onClose={onClose}
      open={show && !!optionclassification.identifier}
      fullScreen={isXSmall ? true : false}
      BackdropProps={{
        classes: {
          root: classes.backDrop,
        },
      }}
      classes={{
        MuiDialogBorder: classes.dialogBorderStyle,
      }}
    >
      {renderDescription()}
      <DialogContent className={classes.contentStyle}>
        {renderRemoteFilters()}
        {renderOptions()}
      </DialogContent>
      {renderApplyButton()}
    </CustomDialog>
  );
};

OptionsList.defaultProps = {
  addCheckedOptionclassification: () => {},
  isLoading: false,
  onClose: () => {},
  onSelect: () => {},
  onToggle: () => {},
  options: [],
  optionclassification: {},
  show: false,
  viewDetails: () => {},
  viewStockInformation: () => {},
  LoadingComponent: LoadingOverlay,
};

OptionsList.propTypes = {
  addCheckedOptionclassification: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  isLoading: PropTypes.bool,
  onClose: PropTypes.func,
  onSelect: PropTypes.func,
  onToggle: PropTypes.func,
  options: PropTypes.array,
  optionclassification: PropTypes.object,
  show: PropTypes.bool,
  viewDetails: PropTypes.func,
  viewStockInformation: PropTypes.func,
  LoadingComponent: PropTypes.elementType,
  OptionComponent: PropTypes.elementType,
  isAdminMode: PropTypes.bool,
};

export default OptionsList;
